import { queryOptions } from '@tanstack/react-query';

import { $api } from '@shared/api';
import { routes } from '@shared/api/routes';

import type {
    Order,
    OrderCurrency,
    OrderCurrencyDto,
    OrderDto,
    OrderEstimate,
    OrderEstimateDto,
    OrderPayment,
    OrderPaymentDto,
    OrderPaymentTotal,
    OrderPaymentTotalDto,
    OrderTotal,
    OrderTotalDto,
} from '../model/types';

type GetOrdersResponse = {
    status: 'success';
    data: {
        items: OrderDto[];
    };
};

export const orderQueryKeys = {
    all: ['orders'] as const,
    currentUser: () => [...orderQueryKeys.all, 'current-user'] as const,
};

const mapCurrency = (currency: OrderCurrencyDto): OrderCurrency => ({
    id: currency.id,
    title: currency.title,
    sign: currency.sign,
    code: currency.code,
    shortCode: currency.short_code,
});

const mapEstimate = (estimate: OrderEstimateDto): OrderEstimate => ({
    id: estimate.id,
    name: estimate.name,
    negotiatedPrice: estimate.negotiated_price,
    completed: estimate.completed,
    paid: estimate.paid,
    toBePaid: estimate.to_be_paid,
    currency: mapCurrency(estimate.currency),
});

const mapTotal = (total: OrderTotalDto): OrderTotal => ({
    negotiatedPrice: total.negotiated_price,
    completed: total.completed,
    paid: total.paid,
    toBePaid: total.to_be_paid,
    currency: mapCurrency(total.currency),
});

const mapPayment = (payment: OrderPaymentDto): OrderPayment => ({
    id: payment.id,
    date: payment.date,
    estimate: payment.estimate,
    amount: payment.amount,
    currency: mapCurrency(payment.currency),
    comment: payment.comment,
});

const mapPaymentTotal = (total: OrderPaymentTotalDto): OrderPaymentTotal => ({
    amount: total.amount,
    currency: mapCurrency(total.currency),
});

const mapOrder = (order: OrderDto): Order => ({
    id: order.id,
    name: order.name,
    estimates: order.estimates.map(mapEstimate),
    totals: order.totals.map(mapTotal),
    payments: order.payments.map(mapPayment),
    paymentTotals: order.payment_totals.map(mapPaymentTotal),
});

export const ordersApi = {
    getOrders: async ({ signal }: { signal?: AbortSignal } = {}) => {
        const response = await $api.get<GetOrdersResponse>(routes.profileOrders(), { signal });

        return response.data.data.items.map(mapOrder);
    },
    getOrdersQueryOptions: () =>
        queryOptions({
            queryKey: orderQueryKeys.currentUser(),
            queryFn: ({ signal }) => ordersApi.getOrders({ signal }),
        }),
};
